<p-toast role="status" aria-live="polite" aria-atomic="true"></p-toast>

<ng-template #alertBanner let-alert>
  @if (isReviewMode()) {
    @if (!alert.resolved) {
      <div class="alert-question-banner flex align-items-center justify-content-between gap-3 mb-3" [attr.data-alert-id]="alert.id">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-bell text-red-500" aria-hidden="true"></i>
          <span class="text-sm text-red-700 font-semibold">Risposta anomala segnalata</span>
        </div>
        <div class="alert-actions">
          @if (!alert.disabled) {
            <p-button label="Prendi in carico" icon="pi pi-user-plus" severity="warn" size="small"
              [loading]="!!alertActionLoading()['take_' + alert.id]" (onClick)="takeInChargeAlert(alert)" />
          }
          <p-button label="Risolto" icon="pi pi-check" severity="success" size="small"
            [loading]="!!alertActionLoading()['resolve_' + alert.id]" (onClick)="resolveAlert(alert)" />
        </div>
      </div>
    } @else {
      <div class="alert-resolved-banner flex align-items-center gap-2 mb-3" [attr.data-alert-id]="alert.id">
        <i class="pi pi-check-circle" aria-hidden="true"></i>
        <span class="text-sm">Avviso risolto</span>
      </div>
    }
  }
</ng-template>

<main class="wizard-container" [class.read-only-mode]="isReadOnly()">
  <!-- Breadcrumb -->
  <div class="mb-4">
    <p-breadcrumb
      styleClass="custom-breadcrumb"
      [model]="breadcrumbItems()"
      [home]="{ icon: 'pi pi-home', routerLink: '/axa/professionals-questionnaire-attachments', ariaLabel: 'Home' }"
      ariaLabel="Breadcrumb navigation"
    >
    </p-breadcrumb>
  </div>

  @if (isLoading()) {
    <div class="flex flex-column align-items-center justify-content-center w-full" style="min-height: 50vh;">
      <p-progress-spinner strokeWidth="3" aria-hidden="true"></p-progress-spinner>
      <p class="text-xl text-500 mt-3 font-medium">Caricamento questionario...</p>
    </div>
  }

  @if (!isLoading() && questionnaire(); as data) {
    <!-- Page Header -->
    <section class="page-header">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-file-edit header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 class="m-0">{{ data.name }}</h1>
            @if (data.tooltip) {
              <p class="subtitle mt-2 mb-0 text-600 font-normal">
                {{ data.tooltip }}
              </p>
            }
            <p class="subtitle">Completa le informazioni richieste per procedere</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Read-Only Mode Banner -->
    @if (isReadOnly()) {
      <div class="read-only-banner" role="alert">
        <div class="flex align-items-center gap-3 mb-2">
          <i class="pi pi-eye" aria-hidden="true"></i>
          <strong>Modalità Sola Lettura</strong>
        </div>
        <span>Stai visualizzando un questionario già compilato. I campi non sono modificabili.</span>
      </div>
    }

    <!-- Step Content -->
    <div class="step-content"
      ><form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="flex flex-column gap-4">
          @for (question of getActiveQuestions(data.questions); track question.id || $index; let i = $index) {
            <section
              class="field-group"
              [class.alert-question]="question.type !== 'MULTISELECT' && hasActiveQuestionAlert(question.id)"
              [attr.aria-labelledby]="'question-title-' + question.id"
              [attr.aria-describedby]="
                getControl(question.id).invalid && getControl(question.id).touched
                  ? 'question-error-' + question.id
                  : null
              "
            >
              <!-- HEADER DOMANDA -->
              <label
                [attr.id]="'question-title-' + question.id"
                [attr.for]="'input-' + question.id"
                class="font-semibold block mb-2"
              >
                <span class="text-primary mr-2">{{ i + 1 }}.</span>
                {{ question.text }}
                @if (question.mandatory && question.type !== 'MULTISELECT') {
                  <span class="text-red-500" aria-hidden="true">*</span>
                  <span class="visually-hidden">obbligatorio</span>
                }
              </label>
              @if (question.tooltip) {
                <p class="text-600 text-sm font-normal mt-0 mb-2 line-height-3">
                  {{ question.tooltip }}
                </p>
              }

              @if (question.type !== 'MULTISELECT') {
                @for (alert of getQuestionAlerts(question.id); track alert.id) {
                  <ng-container [ngTemplateOutlet]="alertBanner" [ngTemplateOutletContext]="{ $implicit: alert }" />
                }
              }

              <!-- BOX RISPOSTA -->
              <div class="field-input"
                [ngClass]="{
                  'error-state': getControl(question.id).invalid && getControl(question.id).touched
                }"
              >
                <!-- SINGLESELECT -->
                @if (isQuestionType(question, 'SINGLESELECT')) {
                  <fieldset class="radio-fieldset">
                    <legend class="visually-hidden" [id]="'question-title-' + question.id">
                      {{ question.text }}
                    </legend>
                    <div
                      class="flex flex-column gap-3"
                      role="radiogroup"
                      [attr.aria-labelledby]="'question-title-' + question.id"
                      [attr.aria-required]="question.mandatory ? 'true' : null"
                      [attr.aria-invalid]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'true'
                          : 'false'
                      "
                      [attr.aria-describedby]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'question-error-' + question.id
                          : null
                      "
                    >
                    @for (option of getActiveOptions(question); track option.id) {
                      <!-- Usa l'ID dell'opzione come valore, ma il controllo è della domanda -->
                      <div
                        class="flex align-items-center bg-white p-2 shadow-sm border-round-lg border-1 border-100 hover:border-primary transition-colors"
                        [class.cursor-pointer]="!isReadOnly()"
                        (click)="!isReadOnly() && form.get(question.id)?.setValue(option.id)"
                      >
                        <p-radio-button
                          [name]="question.id"
                          [inputId]="question.id + option.id"
                          [value]="option.id"
                          [formControlName]="question.id"
                          (onClick)="$event.originalEvent.stopPropagation()"
                          [ariaLabel]="option.text"
                        >
                        </p-radio-button>

                        <label
                          [for]="question.id + option.id"
                          class="w-full font-medium cursor-pointer ml-3"
                          (click)="$event.preventDefault()"
                        >
                          {{ option.text }}
                        </label>
                      </div>
                      <!-- Campo commento per opzioni con requiresComment -->
                      @if (option.requiresComment && isCommentRequired(question.id, option.id)) {
                        <div class="comment-input-field">
                          <label [for]="question.id + '_comment_' + option.id" class="block text-sm font-semibold mb-1">
                            <i class="pi pi-comment mr-1" aria-hidden="true"></i>Commento obbligatorio. Specificare le motivazioni della risposta fornita:
                          </label>
                          <input
                            pInputText
                            [formControlName]="question.id + '_comment_' + option.id"
                            [id]="question.id + '_comment_' + option.id"
                            class="w-full"
                            placeholder="Inserisci commento..."
                            [attr.aria-required]="'true'"
                          />
                          @if (getControl(question.id + '_comment_' + option.id).invalid && getControl(question.id + '_comment_' + option.id).touched) {
                            @if (getControl(question.id + '_comment_' + option.id).hasError('specialChars')) {
                              <small class="text-red-500"><i class="pi pi-exclamation-circle mr-1"></i>Caratteri non permessi</small>
                            } @else {
                              <small class="text-red-500"><i class="pi pi-exclamation-circle mr-1"></i>Commento obbligatorio</small>
                            }
                          }
                        </div>
                      }
                    }
                    </div>
                    @if (getControl(question.id).invalid && getControl(question.id).touched && !getControl(question.id).hasError('specialChars')) {
                      <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Seleziona un'opzione</small>
                    }
                    <!-- Opzioni storiche (cancellate logicamente): visibili solo in readonly se selezionate in precedenza -->
                    @for (option of getInactiveSelectedOptions(question); track option.id) {
                      <div class="flex align-items-center p-2 border-round-lg border-1 border-dashed border-orange-300 bg-orange-50 opacity-80">
                        <p-radio-button [name]="question.id" [value]="option.id" [formControlName]="question.id" [inputId]="question.id + option.id + '_inactive'" [disabled]="true"></p-radio-button>
                        <label class="ml-3 font-medium text-orange-700 line-through">{{ option.text }}</label>
                        <span class="ml-2 text-xs text-orange-500">(opzione non più disponibile)</span>
                      </div>
                    }
                  </fieldset>
                }

                <!-- MULTISELECT -->
                @if (isQuestionType(question, 'MULTISELECT')) {
                  <fieldset class="checkbox-fieldset">
                    <legend class="visually-hidden" [id]="'question-title-' + question.id">
                      {{ question.text }}
                    </legend>
                    <div
                      class="flex flex-column gap-3"
                      role="group"
                      [attr.aria-labelledby]="'question-title-' + question.id"
                      [attr.aria-required]="question.mandatory ? 'true' : null"
                      [attr.aria-invalid]="
                        getControl(question.id).invalid && getControl(question.id).touched
                          ? 'true'
                          : 'false'
                    "
                    [attr.aria-describedby]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : null
                    "
                  >
                    @for (option of getActiveOptions(question); track option.id) {
                      <div class="option-review" [attr.data-option-id]="option.id"
                        [class.alert-option]="hasActiveOptionAlert(question.id, option.id)">
                      <div
                        class="flex align-items-center bg-white p-2 shadow-sm border-round-lg border-1 border-100 transition-all hover:border-primary"
                        [class.cursor-pointer]="!isReadOnly()"
                        [class.border-primary]="isOptionChecked(question.id, option.id)"
                        (click)="!isReadOnly() && toggleMultiOption(question.id, option.id)"
                      >
                        <p-checkbox
                          [inputId]="'check-' + question.id + option.id"
                          [binary]="true"
                          [ngModel]="isOptionChecked(question.id, option.id)"
                          [ngModelOptions]="{ standalone: true }"
                          [disabled]="isReadOnly()"
                          (onChange)="!isReadOnly() && toggleMultiOption(question.id, option.id); $event.originalEvent?.stopPropagation()"
                          (click)="$event.stopPropagation()"
                          class="mr-3"
                          [ariaLabel]="option.text"
                        >
                        </p-checkbox>

                        <label
                          [for]="'check-' + question.id + option.id"
                          class="w-full font-medium cursor-pointer"
                          (click)="$event.preventDefault()"
                        >
                          {{ option.text }}
                        </label>
                      </div>
                      @for (alert of getOptionAlerts(question.id, option.id); track alert.id) {
                        <ng-container [ngTemplateOutlet]="alertBanner" [ngTemplateOutletContext]="{ $implicit: alert }" />
                      }
                      <!-- Campo commento per opzioni con requiresComment -->
                      @if (option.requiresComment && isMultiCommentRequired(question.id, option.id)) {
                        <div class="comment-input-field">
                          <label [for]="question.id + '_comment_' + option.id" class="block text-sm font-semibold mb-1">
                            <i class="pi pi-comment mr-1" aria-hidden="true"></i>Commento obbligatorio: specificiare le motivazioni della risposta fornita:
                          </label>
                          <input
                            pInputText
                            [formControlName]="question.id + '_comment_' + option.id"
                            [id]="question.id + '_comment_' + option.id"
                            class="w-full"
                            placeholder="Inserisci commento..."
                            [attr.aria-required]="'true'"
                          />
                          @if (getControl(question.id + '_comment_' + option.id).invalid && getControl(question.id + '_comment_' + option.id).touched) {
                            @if (getControl(question.id + '_comment_' + option.id).hasError('specialChars')) {
                              <small class="text-red-500"><i class="pi pi-exclamation-circle mr-1"></i>Caratteri non permessi</small>
                            } @else {
                              <small class="text-red-500"><i class="pi pi-exclamation-circle mr-1"></i>Commento obbligatorio</small>
                            }
                          }
                        </div>
                      }
                      </div>
                    }
                  </div>
                  @if (getControl(question.id).invalid && getControl(question.id).touched && !getControl(question.id).hasError('specialChars')) {
                    <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Seleziona almeno un'opzione</small>
                  }
                  <!-- Opzioni storiche (cancellate logicamente): visibili solo in readonly se selezionate in precedenza -->
                  @for (option of getInactiveSelectedOptions(question); track option.id) {
                    <div class="option-review" [attr.data-option-id]="option.id"
                      [class.alert-option]="hasActiveOptionAlert(question.id, option.id)">
                    <div class="flex align-items-center p-2 border-round-lg border-1 border-dashed border-orange-300 bg-orange-50 opacity-80">
                      <p-checkbox [binary]="true" [ngModel]="true" [ngModelOptions]="{standalone: true}" [disabled]="true" class="mr-3"></p-checkbox>
                      <label class="font-medium text-orange-700 line-through">{{ option.text }}</label>
                      <span class="ml-2 text-xs text-orange-500">(opzione non più disponibile)</span>
                    </div>
                    @for (alert of getOptionAlerts(question.id, option.id); track alert.id) {
                      <ng-container [ngTemplateOutlet]="alertBanner" [ngTemplateOutletContext]="{ $implicit: alert }" />
                    }
                    </div>
                  }
                  </fieldset>
                }

                <!-- TEXT -->
                @if (isQuestionType(question, 'TEXT')) {
                  <p class="text-sm text-color-secondary mb-2" [id]="'text-help-'+question.id">
                    <i class="pi pi-info-circle mr-1"></i>
                    Non inserire tag HTML o codice (es. &lt;script&gt;).
                  </p>
                  <input
                    [attr.id]="'input-' + question.id"
                    type="text"
                    pInputText
                    [formControlName]="question.id"
                    placeholder="Inserisci risposta..."
                    class="w-full"
                    [attr.aria-label]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="'text-help-'+question.id + ' ' + (
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : ''
                    )    ? 'question-error-' + question.id
                        : null
                    "
                  />
                  @if (getControl(question.id).invalid && getControl(question.id).touched) {
                    <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Campo obbligatorio</small>
                  }
                }

                <!-- NUMBER -->
                @if (isQuestionType(question, 'NUMBER')) {
                  <p-input-number
                    [inputId]="'input-' + question.id"
                    [formControlName]="question.id"
                    [useGrouping]="false"
                    placeholder="Inserisci valore numerico..."
                    styleClass="w-full"
                    [ariaLabel]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : null
                    "
                  ></p-input-number>
                  @if (getControl(question.id).invalid && getControl(question.id).touched) {
                    <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Campo obbligatorio</small>
                  }
                }

                <!-- DATE -->
                @if (isQuestionType(question, 'DATE')) {
                  <p class="text-sm text-color-secondary mb-2" [id]="'date-help-'+question.id">
                    <i class="pi pi-info-circle mr-1"></i>
                    Formato: YYYY-MM-DD (esempio: 2026-04-21)
                  </p>
                  <p-date-picker
                    [inputId]="'input-' + question.id"
                    [formControlName]="question.id"
                    placeholder="YYYY-MM-DD"
                    dateFormat="yy-mm-dd"
                    dataType="string"
                    styleClass="w-full"
                    [ariaLabel]="question.text"
                    [attr.aria-required]="question.mandatory ? 'true' : null"
                    [attr.aria-invalid]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'true'
                        : 'false'
                    "
                    [attr.aria-describedby]="'date-help-'+question.id + ' ' + (
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : ''
                    )"
                  ></p-date-picker>
                  @if (getControl(question.id).invalid && getControl(question.id).touched) {
                    <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Campo obbligatorio</small>
                  }
                }

                <!-- BOOLEAN -->
                @if (isQuestionType(question, 'BOOLEAN')) {
                  <div
                    class="flex align-items-center gap-3 p-2"
                    role="group"
                    [attr.aria-labelledby]="'question-title-' + question.id"
                    [attr.aria-describedby]="
                      getControl(question.id).invalid && getControl(question.id).touched
                        ? 'question-error-' + question.id
                        : null
                    "
                  >
                    <p-checkbox
                      [formControlName]="question.id"
                      [binary]="true"
                      [inputId]="question.id"
                      [ariaLabel]="question.options[0]?.text || question.text"
                    ></p-checkbox>
                    <label [for]="question.id" class="cursor-pointer font-medium">{{
                      question.options[0]?.text
                    }}</label>
                  </div>
                  @if (getControl(question.id).invalid && getControl(question.id).touched && !getControl(question.id).hasError('specialChars')) {
                    <small [id]="'question-error-' + question.id" class="text-red-500" role="alert">Campo obbligatorio</small>
                  }
                }

                <!-- CAMPO ALTRO DINAMICO -->
                @if (isOtherSelected(question.id)) {
                  <div
                    class="mt-4 p-3 border-round-lg border-left-3 border-primary bg-white shadow-1 animate-fadein"
                  >
                    <label
                      [for]="question.id + '_otherText'"
                      class="block font-semibold mb-2 text-primary"
                      >Specifica il dettaglio per 'Altro':</label
                    >
                    <input
                      pInputText
                      [formControlName]="question.id + '_otherText'"
                      [id]="question.id + '_otherText'"
                      class="w-full"
                      placeholder="Dettagli..."
                      [attr.aria-required]="'true'"
                      [attr.aria-invalid]="
                        getControl(question.id + '_otherText').invalid &&
                        getControl(question.id + '_otherText').touched
                          ? 'true'
                          : 'false'
                      "
                      [attr.aria-describedby]="
                        getControl(question.id + '_otherText').invalid &&
                        getControl(question.id + '_otherText').touched
                          ? 'question-other-error-' + question.id
                          : null
                      "
                    />
                    @if (
                      getControl(question.id + '_otherText').invalid &&
                      getControl(question.id + '_otherText').touched
                    ) {
                      @if (getControl(question.id + '_otherText').hasError('specialChars')) {
                        <small
                          [attr.id]="'question-other-error-' + question.id"
                          class="text-red-500 block mt-2 font-semibold"
                          role="alert"
                          aria-live="assertive"
                        >
                          <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i> ⚠️
                          Caratteri non permessi (non usare: &lt; &gt; " ' &amp; / \)
                        </small>
                      } @else {
                        <small
                          [attr.id]="'question-other-error-' + question.id"
                          class="p-error block mt-2 font-semibold"
                          role="alert"
                          aria-live="assertive"
                        >
                          <i class="pi pi-exclamation-circle mr-1" aria-hidden="true"></i>
                          Specifica il dettaglio richiesto
                        </small>
                      }
                    }
                  </div>
                }
              </div>
            </section>
          }
        </div>

        <!-- Form Actions -->
        <div class="form-actions mt-6">
          @if (isReadOnly()) {
            <div class="actions-left">
              <button
                pButton
                type="button"
                label="Torna Indietro"
                icon="pi pi-arrow-left"
                class="p-button-outlined"
                (click)="goBack()"
              ></button>
            </div>
            <div class="actions-right">
              <button
                pButton
                type="button"
                label="Scarica PDF"
                icon="pi pi-file-pdf"
                class="p-button-outlined"
                (click)="downloadPdf()"
                [attr.aria-label]="'Scarica PDF questionario'"
              ></button>
            </div>
          } @else {
            <div class="actions-left">
              <button
                pButton
                type="button"
                label="Torna Indietro"
                icon="pi pi-arrow-left"
                class="p-button-outlined p-button-secondary"
                (click)="goBack()"
              ></button>
            </div>
            
            <div class="actions-right">
              <button
                pButton
                type="button"
                label="Salva Bozza"
                icon="pi pi-save"
                class="p-button-outlined"
                [disabled]="isSavingDraft() || isSubmitting()"
                [loading]="isSavingDraft()"
                (click)="saveDraft()"
              ></button>
              
              <button
                pButton
                type="submit"
                label="Salva e Prosegui"
                icon="pi pi-arrow-right"
                iconPos="right"
                [disabled]="isSubmitting() || isSavingDraft()"
                [loading]="isSubmitting()"
              ></button>
            </div>
          }
        </div>
      </form>
    </div>
  }
</main>
